import React, {
  useCallback,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Autocomplete,
  TextField,
  TableSortLabel,
  Box,
  Snackbar,
  Grid,
  Tooltip,
  IconButton,
  Badge,
  Chip,
} from "@mui/material";
import TextLabel from "components/Common/TextLabel/TextLabel";
import classes from "../../OMTBulkEdit/Common.module.css";
import {
  getAllOMTHardwareStatuses,
  getOMTHardware,
  saveHardware,
} from "services/userServices";
import BackdropComponent from "components/Spinner/Spinner";
import {
  CustomButton,
  CustomTooltip,
} from "components/RosterConfig/RosterConfigStyle";
import { useDispatch } from "react-redux";
import { SnackBarStoreAction } from "Store/SnackBar";
import { fontStyles } from "components/Common/commonStyles";
import { AttachFile, UnfoldMoreOutlined } from "@mui/icons-material";
import OMTAttachments from "../OMTAttachments";
import { red } from "@mui/material/colors";
import PropTypes from "prop-types";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useColumnFilter } from "../useColumnFilter";
import ColumnFilterPopover from "../ColumnFilterPopover";

const EXTRA_EQUIPMENT_OPTIONS = [
  "Monitor",
  "Keyboard",
  "Mouse",
  "Cables",
  "Headset",
  "Token",
  "Webcam",
];

const getValue = (key, value, data) => {
  return data?.find((item) => item[key] === value);
};

const Hardware = forwardRef(function Hardware(props, ref) {
  const {
    rosterId,
    asdLeadOracleId,
    taskTypeId,
    scheduleType,
    editAccess,
    agentOracleId,
    onDirtyChange,
    noRosterIdClassName,
    isUploadMode,
    isNoRosterIdSearch,
  } = props;
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [autoCompleteOptions, setautoCompleteOptions] = useState([]);
  const [originalHardwareData, setOriginalHardwareData] = useState([]);
  const [editedRows, setEditedRows] = useState([]);
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");
  const [loading, setLoading] = useState(true);
  const [openAttachmentDialog, setOpenAttachmentDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState();

  const showSnack = (message, severity) => {
    const obj = {
      open: true,
      message: message,
      severity: severity,
    };
    dispatch(SnackBarStoreAction.updateSnack(obj));
  };

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  useEffect(() => {
    const dirty =
      JSON.stringify(editedRows) !== JSON.stringify(originalHardwareData);
    onDirtyChange?.(dirty);
  }, [editedRows, originalHardwareData]);

  useImperativeHandle(ref, () => ({
    save: () => handleSave(),
  }));

  function replaceNulls(data) {
    const fieldsToUpdate = [
      "Cables",
      "CPUTower",
      "Headset",
      "Keyboard",
      "Webcam",
      "AdditionalRequestHeadset",
      "Monitor",
      "Mouse",
      "YubiKeyToken",
    ];

    return data.map((item) => {
      const updatedItem = { ...item };
      fieldsToUpdate.forEach((field) => {
        if (updatedItem[field] === null) {
          updatedItem[field] = field !== "AdditionalRequestHeadset" ? 1 : false;
        }
      });
      if (
        updatedItem.PCNotDelivered === null ||
        updatedItem.PCNotDelivered === undefined
      )
        updatedItem.PCNotDelivered = false;
      if (
        updatedItem.PersonalEquipment === null ||
        updatedItem.PersonalEquipment === undefined
      )
        updatedItem.PersonalEquipment = "";
      if (
        updatedItem.FailedToReceiveEquipment === null ||
        updatedItem.FailedToReceiveEquipment === undefined
      )
        updatedItem.FailedToReceiveEquipment = false;
      return updatedItem;
    });
  }

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      if (
        (!rosterId || rosterId === "") &&
        (!noRosterIdClassName || noRosterIdClassName === "") &&
        (!agentOracleId || agentOracleId === "" || noRosterIdClassName)
      ) {
        setRows([]);
        setEditedRows([]);
        setOriginalHardwareData([]);
        setLoading(false);
        return;
      }

      try {
        if (!cancelled) {
          setLoading(true);
        }

        const [hardwareStatusResponse, hardwareResultsResponse] =
          await Promise.all([
            getAllOMTHardwareStatuses(),
            getOMTHardware(
              rosterId,
              asdLeadOracleId,
              taskTypeId,
              scheduleType,
              agentOracleId,
              noRosterIdClassName,
              isNoRosterIdSearch,
            ),
          ]);

        if (cancelled) return;

        if (hardwareStatusResponse?.status === 200) {
          if (!cancelled) {
            setautoCompleteOptions(hardwareStatusResponse?.data?.data);
          }
        }

        if (hardwareResultsResponse?.status === 200) {
          if (!cancelled) {
            const processed = replaceNulls(hardwareResultsResponse?.data?.data);
            setRows(processed);
            setEditedRows(processed);
            setOriginalHardwareData(JSON.parse(JSON.stringify(processed)));
          }
        }
      } catch (error) {
        if (!cancelled) {
          if (error?.response?.status === 404 || error?.statusCode === 404) {
            setRows([]);
            setEditedRows([]);
            setOriginalHardwareData([]);
            if (rosterId || noRosterIdClassName) {
              showSnack(
                "No hardware records found for the selected filters.",
                "info",
              );
            }
          } else {
            console.log(error);
            showSnack("Internal error occured", "error");
          }
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [rosterId, agentOracleId, noRosterIdClassName]);

  const cellPadding = {
    padding: "6px 8px",
  };

  const handleChange = useCallback(
    (index, field, value) => {
      const updated = [...editedRows];

      updated[index] = {
        ...updated[index],
        [field]: value,
      };

      if (field === "PCNotDelivered" && value === true)
        updated[index].PersonalEquipment = "";

      if (field === "Headset") {
        const headsetStatusName = autoCompleteOptions.find(
          (opt) => opt.statusId === value,
        )?.statusName;
        if (
          !["Faulty", "Missing", "Incompatible"].includes(headsetStatusName)
        ) {
          updated[index].AdditionalRequestHeadset = false;
        }
      }

      setEditedRows(updated);
    },
    [editedRows, autoCompleteOptions],
  );

  const handleReset = () => {
    setEditedRows(rows);
    setOrderBy("");
    setOrder("asc");
  };

  async function fetchHardwareData() {
    if (!rosterId && !noRosterIdClassName) {
      return;
    }

    const response = await getOMTHardware(
      rosterId,
      asdLeadOracleId,
      taskTypeId,
      scheduleType,
      agentOracleId,
      noRosterIdClassName,
      isNoRosterIdSearch,
    );
    if (response?.status === 200) {
      const processedData = replaceNulls(response?.data?.data);
      setRows(replaceNulls(response?.data?.data));
      setEditedRows(replaceNulls(response?.data?.data));
      setOriginalHardwareData(JSON.parse(JSON.stringify(processedData)));
    }
  }

  useEffect(() => {
    let cancelled = false;

    const refreshAfterAttachment = async () => {
      if (openAttachmentDialog === false) {
        try {
          await fetchHardwareData();
        } catch (error) {
          if (!cancelled) {
            console.error("Failed to refresh hardware data:", error);
          }
        }
      }
    };

    refreshAfterAttachment();

    return () => {
      cancelled = true;
    };
  }, [openAttachmentDialog]);

  const handleSave = async () => {
    const hasChanges =
      JSON.stringify(editedRows) !== JSON.stringify(originalHardwareData);

    if (!hasChanges) {
      showSnack("No changes to save", "info");
      return true;
    }

    try {
      setLoading(true);

      setRows(editedRows);

      const mappedRows = editedRows.map((row) => ({
        ...row,
        OMTScheduleStatusType: row.ScheduleType || "",
        AdditionalRequestHeadset: row.AdditionalRequestHeadset
          ? "True"
          : "False",
        PCNotDelivered: row.PCNotDelivered ? "True" : "False",
        PersonalEquipment: row.PersonalEquipment || "",
        FailedToReceiveEquipment: row.FailedToReceiveEquipment
          ? "True"
          : "False",
      }));

      const payload = { OMTHardWareList: mappedRows };

      const saveHardwareResponse = await saveHardware(
        payload,
        localStorage.getItem("User"),
      );

      if (saveHardwareResponse?.statusCode === 200) {
        setOriginalHardwareData(JSON.parse(JSON.stringify(editedRows)));
        showSnack("Saved Successfully", "success");
        fetchHardwareData();
        return true;
      }

      showSnack("Failed to Save", "error");
      return false;
    } catch (error) {
      showSnack("Internal error occurred", "error");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    const isAsc = orderBy === field && order === "asc";
    const newOrder = isAsc ? "desc" : "asc";
    setOrderBy(field);
    setOrder(newOrder);

    const sorted = [...editedRows].sort((a, b) => {
      if (a[field] < b[field]) return newOrder === "asc" ? -1 : 1;
      if (a[field] > b[field]) return newOrder === "asc" ? 1 : -1;
      return 0;
    });

    setEditedRows(sorted);
  };

  const isAdditionalRequestHeadsetDisabled = (row) => {
    if (parseInt(row?.isBYOD) === 1) {
      return true;
    }
    return ![2, 3, 4].includes(row?.Headset);
  };

  const handleAttachmentOpen = (row) => {
    setOpenAttachmentDialog(true);
    setSelectedRow(row);
  };

  const traineeStatusFilter = useColumnFilter(editedRows, "TraineeStatus");

  const filteredHardwareData = useMemo(() => {
    return editedRows
      .map((row, originalIndex) => ({ row, originalIndex }))
      .filter(({ row }) => traineeStatusFilter.matches(row));
  }, [editedRows, traineeStatusFilter.selectedValues]);

  return (
    <>
      {loading ? <BackdropComponent /> : null}
      <Box sx={{ ...fontStyles }}>
        <Paper sx={{ padding: 2 }}>
          <TableContainer className={classes.container}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "white" }}>
                  <TableCell
                    sx={{ ...fontStyles, ...cellPadding }}
                    sortDirection={orderBy === "ClassRosterId" ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === "ClassRosterId"}
                      direction={orderBy === "ClassRosterId" ? order : "asc"}
                      onClick={() => handleSort("ClassRosterId")}
                      IconComponent={() => (
                        <UnfoldMoreOutlined
                          direction={
                            orderBy === "ClassRosterId" ? order : "asc"
                          }
                        />
                      )}
                    >
                      <TextLabel text={isUploadMode ? "Class Name" : "RID"} />
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{ ...fontStyles, ...cellPadding }}
                    sortDirection={orderBy === "EmployeeName" ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === "EmployeeName"}
                      direction={orderBy === "EmployeeName" ? order : "asc"}
                      onClick={() => handleSort("EmployeeName")}
                      IconComponent={() => (
                        <UnfoldMoreOutlined
                          direction={orderBy === "EmployeeName" ? order : "asc"}
                        />
                      )}
                    >
                      <TextLabel text="Name" />
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "white",
                      width: "150px",
                      alignItems: "center",
                    }}
                    onClick={() => handleSort("ScheduleType")}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <TextLabel
                        text={"Schedule Type"}
                        className={`${classes.fontStyles} ${classes.cellPadding}`}
                      />
                      <UnfoldMoreOutlined
                        sx={{
                          fontSize: 18,
                          color: "#3F5765",
                          cursor: "pointer",
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "white",
                      width: "150px",
                      alignItems: "center",
                    }}
                    onClick={() => handleSort("ScheduleStatus")}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <TextLabel
                        text={"Schedule Status"}
                        className={`${classes.fontStyles} ${classes.cellPadding}`}
                      />
                      <UnfoldMoreOutlined
                        sx={{
                          fontSize: 18,
                          color: "#3F5765",
                          cursor: "pointer",
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "white",
                      width: "150px",
                      alignItems: "center",
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <TextLabel
                        text={"Trainee Status"}
                        className={`${classes.fontStyles} ${classes.cellPadding}`}
                      />
                      <IconButton
                        onClick={traineeStatusFilter.openFilter}
                        size="small"
                        color={
                          traineeStatusFilter.selectedValues.length
                            ? "primary"
                            : "default"
                        }
                      >
                        <FilterListIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ ...fontStyles, ...cellPadding }}>
                    <TextLabel text="PC Not Delivered" />
                  </TableCell>
                  <TableCell sx={{ ...fontStyles, ...cellPadding }}>
                    <TextLabel text="Personal Equipment" />
                  </TableCell>
                  <TableCell sx={{ ...fontStyles, ...cellPadding }}>
                    <TextLabel text="Failed to receive equipment" />
                  </TableCell>
                  <TableCell sx={{ ...fontStyles, ...cellPadding }}>
                    <TextLabel text="Cables" />
                  </TableCell>
                  <TableCell sx={{ ...fontStyles, ...cellPadding }}>
                    <TextLabel text="CPU / Tower" />
                  </TableCell>
                  <TableCell sx={{ ...fontStyles, ...cellPadding }}>
                    <TextLabel text="Headset" />
                  </TableCell>
                  <TableCell sx={{ ...fontStyles, ...cellPadding }}>
                    <TextLabel text="Additional Request Headset" />
                  </TableCell>
                  <TableCell sx={{ ...fontStyles, ...cellPadding }}>
                    <TextLabel text="Keyboard" />
                  </TableCell>
                  <TableCell sx={{ ...fontStyles, ...cellPadding }}>
                    <TextLabel text="Webcam" />
                  </TableCell>
                  <TableCell sx={{ ...fontStyles, ...cellPadding }}>
                    <TextLabel text="Monitor" />
                  </TableCell>
                  <TableCell sx={{ ...fontStyles, ...cellPadding }}>
                    <TextLabel text="Mouse" />
                  </TableCell>
                  <TableCell sx={{ ...fontStyles, ...cellPadding }}>
                    <TextLabel text="YubiKeyToken" />
                  </TableCell>
                  <TableCell sx={{ ...fontStyles, ...cellPadding }}>
                    <TextLabel text="Comments" />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredHardwareData.map(({ row, originalIndex }) => {
                  const isDisabled =
                    row.AttendanceNoShow === true ||
                    row.AttendanceISPOutage === true;
                  const isHardwareDisabled =
                    isDisabled || row.PCNotDelivered === true;
                  const selectedEquipment = (row.PersonalEquipment || "")
                    .split(",")
                    .filter(Boolean);

                  return (
                    <TableRow key={`${row.ClassRosterId}-${originalIndex}`}>
                      <TableCell
                        sx={{ ...fontStyles, ...cellPadding, width: 120 }}
                      >
                        <Grid container>
                          <Grid item xs={12}>
                            <TextLabel
                              text={
                                noRosterIdClassName
                                  ? row.NoRosterIdClassName
                                  : row.ClassRosterId
                              }
                              fontSize={14}
                              subHeading
                            />
                          </Grid>
                          <Grid item xs={12} sx={{ mt: 1 }}>
                            <Tooltip title="Attach File">
                              <Badge
                                badgeContent={row?.AttachmentCount || 0}
                                color="primary"
                                sx={{
                                  "& .MuiBadge-badge": {
                                    color: "#ffffff",
                                    backgroundColor: red[700],
                                  },
                                }}
                              >
                                <IconButton
                                  onClick={() => handleAttachmentOpen(row)}
                                  size="small"
                                  disableRipple
                                  disabled={isDisabled}
                                  sx={{
                                    backgroundColor: "#3299ff",
                                    borderRadius: 1,
                                  }}
                                  aria-label="attach file"
                                >
                                  <AttachFile
                                    fontSize="small"
                                    sx={{ color: "#ffffff" }}
                                  />
                                </IconButton>
                              </Badge>
                            </Tooltip>
                          </Grid>
                        </Grid>
                      </TableCell>
                      <TableCell sx={{ ...fontStyles, ...cellPadding }}>
                        <TextLabel text={row.EmployeeName} description />
                      </TableCell>
                      <TableCell>
                        <TextLabel
                          text={row.ScheduleType}
                          description
                        ></TextLabel>
                      </TableCell>
                      <TableCell>
                        <TextLabel
                          text={row.ScheduleStatus}
                          description
                        ></TextLabel>
                      </TableCell>
                      <TableCell>
                        <TextLabel
                          text={row.TraineeStatus}
                          description
                        ></TextLabel>
                      </TableCell>
                      <TableCell sx={cellPadding} align="center">
                        <Checkbox
                          checked={row.PCNotDelivered || false}
                          disabled={isDisabled}
                          onChange={(e) =>
                            handleChange(
                              originalIndex,
                              "PCNotDelivered",
                              e.target.checked,
                            )
                          }
                          sx={fontStyles}
                          size="medium"
                        />
                      </TableCell>
                      <TableCell sx={cellPadding}>
                        <Tooltip
                          title={
                            selectedEquipment.length > 1
                              ? selectedEquipment.join(", ")
                              : ""
                          }
                          placement="top"
                        >
                          <span>
                            <Autocomplete
                              multiple
                              size="small"
                              options={EXTRA_EQUIPMENT_OPTIONS}
                              disabled={
                                isDisabled || row.PCNotDelivered === true
                              }
                              value={selectedEquipment}
                              onChange={(e, newVal) =>
                                handleChange(
                                  originalIndex,
                                  "PersonalEquipment",
                                  newVal.join(","),
                                )
                              }
                              renderTags={(value, getTagProps) => {
                                const visible = 1;
                                return (
                                  <>
                                    {value
                                      .slice(0, visible)
                                      .map((option, i) => (
                                        <Chip
                                          key={option}
                                          label={option}
                                          size="small"
                                          sx={{ ...fontStyles, fontSize: 11 }}
                                          {...getTagProps({ index: i })}
                                        />
                                      ))}
                                    {value.length > visible && (
                                      <Chip
                                        label={`+${value.length - visible}`}
                                        size="small"
                                        sx={{
                                          ...fontStyles,
                                          fontSize: 11,
                                          height: 20,
                                          backgroundColor: "#3299ff",
                                          color: "#fff",
                                          fontWeight: 600,
                                          cursor: "default",
                                        }}
                                      />
                                    )}
                                  </>
                                );
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  size="small"
                                  placeholder={
                                    selectedEquipment.length === 0
                                      ? "Optional"
                                      : ""
                                  }
                                  sx={{
                                    ...fontStyles,
                                    minWidth: 180,
                                    "& input::placeholder": {
                                      color: "rgba(0,0,0,0.87)",
                                      opacity: 1,
                                    },
                                  }}
                                  InputProps={{
                                    ...params.InputProps,
                                    sx: { ...fontStyles },
                                  }}
                                />
                              )}
                              slotProps={{ paper: { sx: fontStyles } }}
                            />
                          </span>
                        </Tooltip>
                      </TableCell>

                      <TableCell sx={cellPadding} align="center">
                        <Checkbox
                          checked={row.FailedToReceiveEquipment || false}
                          disabled={isDisabled}
                          onChange={(e) =>
                            handleChange(
                              originalIndex,
                              "FailedToReceiveEquipment",
                              e.target.checked,
                            )
                          }
                          sx={fontStyles}
                          size="medium"
                        />
                      </TableCell>

                      {["Cables", "CPUTower", "Headset"].map((field) => {
                        const currentValue =
                          getValue(
                            "statusId",
                            row[field],
                            autoCompleteOptions,
                          ) ||
                          autoCompleteOptions.find(
                            (opt) => opt.statusName === "Working",
                          ) ||
                          null;

                        const isNotWorking =
                          currentValue?.statusName !== "Working";

                        return (
                          <TableCell key={field} sx={cellPadding}>
                            <Autocomplete
                              size="small"
                              options={autoCompleteOptions}
                              disableClearable
                              disabled={isHardwareDisabled}
                              getOptionLabel={(option) =>
                                option?.statusName || ""
                              }
                              isOptionEqualToValue={(option, value) =>
                                option?.statusId === value?.statusId
                              }
                              value={currentValue}
                              onChange={(e, newValue) =>
                                handleChange(originalIndex, field, newValue?.statusId)
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  size="small"
                                  sx={{
                                    ...fontStyles,
                                    width: 150,
                                    "& .MuiOutlinedInput-root": {
                                      backgroundColor: isNotWorking
                                        ? "#ffebee"
                                        : "transparent",
                                      "& fieldset": {
                                        borderColor: isNotWorking
                                          ? "#f44336"
                                          : undefined,
                                        borderWidth: isNotWorking ? 2 : 1,
                                      },
                                      "&:hover fieldset": {
                                        borderColor: isNotWorking
                                          ? "#d32f2f"
                                          : undefined,
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor: isNotWorking
                                          ? "#d32f2f"
                                          : undefined,
                                      },
                                    },
                                  }}
                                  InputProps={{
                                    ...params.InputProps,
                                    sx: { ...fontStyles, paddingY: 0.2 },
                                  }}
                                />
                              )}
                              slotProps={{ paper: { sx: fontStyles } }}
                            />
                          </TableCell>
                        );
                      })}

                      <TableCell sx={cellPadding} align="center">
                        <Checkbox
                          checked={row.AdditionalRequestHeadset}
                          disabled={
                            isAdditionalRequestHeadsetDisabled(row) ||
                            isHardwareDisabled
                          }
                          onChange={(e) =>
                            handleChange(
                              originalIndex,
                              "AdditionalRequestHeadset",
                              e.target.checked,
                            )
                          }
                          sx={fontStyles}
                          size="medium"
                        />
                      </TableCell>

                      {[
                        "Keyboard",
                        "Webcam",
                        "Monitor",
                        "Mouse",
                        "YubiKeyToken",
                      ].map((field) => {
                        const currentValue =
                          getValue(
                            "statusId",
                            row[field],
                            autoCompleteOptions,
                          ) ||
                          autoCompleteOptions.find(
                            (opt) => opt.statusName === "Working",
                          ) ||
                          null;

                        const isNotWorking =
                          currentValue?.statusName !== "Working";

                        return (
                          <TableCell key={field} sx={cellPadding}>
                            <Autocomplete
                              size="small"
                              options={autoCompleteOptions}
                              disableClearable
                              disabled={isHardwareDisabled}
                              getOptionLabel={(option) =>
                                option?.statusName || ""
                              }
                              isOptionEqualToValue={(option, value) =>
                                option?.statusId === value?.statusId
                              }
                              value={currentValue}
                              onChange={(e, newValue) =>
                                handleChange(originalIndex, field, newValue?.statusId)
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  size="small"
                                  sx={{
                                    ...fontStyles,
                                    width: 150,
                                    "& .MuiOutlinedInput-root": {
                                      backgroundColor: isNotWorking
                                        ? "#ffebee"
                                        : "transparent",
                                      "& fieldset": {
                                        borderColor: isNotWorking
                                          ? "#f44336"
                                          : undefined,
                                        borderWidth: isNotWorking ? 2 : 1,
                                      },
                                      "&:hover fieldset": {
                                        borderColor: isNotWorking
                                          ? "#d32f2f"
                                          : undefined,
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor: isNotWorking
                                          ? "#d32f2f"
                                          : undefined,
                                      },
                                    },
                                  }}
                                  InputProps={{
                                    ...params.InputProps,
                                    sx: { ...fontStyles, paddingY: 0.5 },
                                  }}
                                />
                              )}
                              slotProps={{ paper: { sx: fontStyles } }}
                            />
                          </TableCell>
                        );
                      })}

                      <TableCell className={classes.minWidth}>
                        <CustomTooltip title={row.Comments || ""}>
                          <TextField
                            size="small"
                            disabled={isDisabled}
                            multiline
                            fullWidth
                            maxRows={4}
                            value={row.Comments || ""}
                            onChange={(e) =>
                              handleChange(originalIndex, "Comments", e.target.value)
                            }
                            InputProps={{ sx: { ...fontStyles } }}
                            helperText={`${row.Comments ? row.Comments.length : 0}/${100}`}
                            inputProps={{ maxLength: 100 }}
                            placeholder="Max 100 Characters"
                          />
                        </CustomTooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <ColumnFilterPopover
            anchorEl={traineeStatusFilter.anchorEl}
            onClose={traineeStatusFilter.closeFilter}
            options={traineeStatusFilter.uniqueValues}
            selectedValues={traineeStatusFilter.selectedValues}
            onToggle={traineeStatusFilter.toggleValue}
            onClear={traineeStatusFilter.clearFilter}
          />
          <Box className={classes.buttonBox}>
            <CustomButton
              variant="contained"
              className={classes.buttonSave}
              size="small"
              onClick={handleSave}
              disabled={!editAccess}
            >
              Save
            </CustomButton>
            <CustomButton
              variant="outlined"
              className={classes.buttonReset}
              size="small"
              onClick={handleReset}
              disabled={!editAccess}
            >
              Reset
            </CustomButton>
          </Box>
        </Paper>
      </Box>
      <Snackbar snack={snack} snackReset={snackReset} />
      {openAttachmentDialog && (
        <OMTAttachments
          openAttachmentDialog={openAttachmentDialog}
          row={selectedRow}
          category="Hardware"
          onClose={() => setOpenAttachmentDialog(false)}
          editAccess={editAccess}
        />
      )}
    </>
  );
});

Hardware.propTypes = {
  rosterId: PropTypes.string,
  asdLeadOracleId: PropTypes.string,
  taskTypeId: PropTypes.string,
  scheduleType: PropTypes.string,
  editAccess: PropTypes.bool,
  agentOracleId: PropTypes.string,
  onDirtyChange: PropTypes.func,
};

export default Hardware;
